import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Accordion, AccordionItem } from "@nextui-org/react";
import { ZButton } from "@/shared/components/button";
import { Upload, File, Trash, ChevronLeft, ChevronRight, RotateCw, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import type { UploadedFile } from "@/shared/types/file-upload";
import type { ChangeEvent } from "react";
import { addNotification } from "@/shared/states/notification";
import { AGENT_TYPES, RiskAssessmentTypes } from "../../types";
import type { AgentSourceFile } from "../../types";
import { useAgentData } from "../../states";
import ZFileName from "@/shared/components/custom/file-upload/FileName";
import { doesFileHasError } from "@/shared/components/custom/file-upload/helper";
import { useNavigate } from "react-router-dom";
import { processDataBasedOnAssessment } from "../upload-dialog/util";
import type { NistCsfControl } from "../../states/cusomize-controls";
import { getCustomizeControlsActions } from "../../states/cusomize-controls";
import { v4 as uuidv4 } from "uuid";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { useIsDemoActive } from "@/shared/states/user";
import { ZProgress } from "@/shared/components/progress";
import type { AxiosProgressEvent } from "axios";
import { cn } from "@/shared/utils/classname-merger";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import { fileErrorTypes } from "@/shared/constants";

interface ReassessDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  onBackToReviewResponse?: () => void;
  // Control-specific props
  control?: NistCsfControl;
  controlIndex?: number;
  onPrevControl?: () => void;
  onNextControl?: () => void;
  totalControls?: number;
  controlName: string;
  controlDescription: string;
  showSaveSuccess?: boolean;
}

const ReassessDocumentsModal = ({
  isOpen,
  onClose,
  agentId,
  onBackToReviewResponse,
  // Control-specific props
  control,
  controlIndex,
  onPrevControl,
  onNextControl,
  totalControls,
  controlName,
  controlDescription,
  showSaveSuccess = false,
}: ReassessDocumentsModalProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isFileUploadError, setIsFileUploadError] = useState<boolean>(false);
  const [permanentFiles, setPermanentFiles] = useState<AgentSourceFile[]>([]);
  const [tempFiles, setTempFiles] = useState<AgentSourceFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDemo = useIsDemoActive();
  
  // Determine if we're in control-specific mode or agent-level mode
  const isControlSpecificMode = !!control;
  
  // Get agent data from store
  const agentData = useAgentData(agentId);
  const { agentType, subType } = agentData || { agentType: AGENT_TYPES.RISK_ASSESSMENT, subType: RiskAssessmentTypes.NIST_CSF_2 };
  
  // Get max files limit based on agent type
  const maxFiles = 30; // Default max files
  
  // Allowed file types
  const allowedFileTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/json",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "text/csv",
  ];
  
  const getLocalFileNameSuffix = (status: string | undefined): string => {
    if (!status) return "";
    switch (status) {
      case "too_large":
        return " (too large)";
      case "too_many_files":
        return " (Excess file. Remove any.)";
      case "failed":
        return " (upload failed)";
      case "duplicate_files":
        return " (duplicate file)";
      // No suffix for "retrying", "inprogress", "success", "gettingurl"
      default:
        return "";
    }
  };
  
  // Load existing files when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset states for the current interaction session
      setFiles([]);
      setUploadProgress({});
      setIsFileUploadError(false);

      let initialPermanentFiles: AgentSourceFile[] = [];
      let initialTempFiles: AgentSourceFile[] = [];

      if (isControlSpecificMode && control) {
        if (control.source_urls) {
          initialPermanentFiles = control.source_urls.map((url, i) => ({
            fileName: control.file_names?.[i] || `File ${i + 1}`,
            url: url,
            fileType: url.split('.').pop()?.toLowerCase() || "unknown",
          }));
        }
        if (control.new_uploaded_files) {
          initialTempFiles = control.new_uploaded_files.map((url, i) => ({
            fileName: control.new_uploaded_file_names?.[i] || `New File ${i + 1}`,
            url: url,
            fileType: url.split('.').pop()?.toLowerCase() || "unknown",
            status: 'success',
            id: uuidv4(),
          }));
        }
      }
      setPermanentFiles(initialPermanentFiles);
      setTempFiles(initialTempFiles);
    }
  }, [isOpen, control, isControlSpecificMode]);
  
  const isFileTooLarge = (file: File, megaBytes = 20) => {
    const sizeLimit = megaBytes * 1024 * 1024;
    return file.size > sizeLimit;
  };
  
  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const newSelectedFiles = Array.from(e.target.files);
    let showFileSizeNotification = false;
    let showFileLimitNotification = false;
    const largeFileNames: string[] = [];
    const newSessionFileEntries: UploadedFile[] = [];
    const newTempUiEntries: AgentSourceFile[] = [];

    const existingFileNames = new Set<string>(tempFiles.map(fileData => fileData.fileName))

    let currentTotalSlotsFilled = permanentFiles.length + tempFiles.length;
    let excessFilesCount = 0;

    for (const file of newSelectedFiles) {
      if (!allowedFileTypes.includes(file.type)) continue;
      const id = uuidv4();
      let status: UploadedFile["status"] = "gettingurl";

      if (isFileTooLarge(file)) {
        status = "too_large";
        if(!largeFileNames.includes(file.name)) largeFileNames.push(file.name);
        showFileSizeNotification = true;
      } else if(existingFileNames.has(file.name)){
        status = "duplicate_files"
      } else if (status === "gettingurl") {
        if (currentTotalSlotsFilled >= maxFiles) {
          status = "too_many_files";
          showFileLimitNotification = true;
          excessFilesCount++;
        } else {
          currentTotalSlotsFilled++; 
        }
      }

      newSessionFileEntries.push({ id, file, preview: URL.createObjectURL(file), status, progressBar: false, type: file.type });
      newTempUiEntries.push({ fileName: file.name, url: "", fileType: file.type, status, id, }); 
    }

    if (newSessionFileEntries.length > 0) {
        setFiles(prev => [...prev, ...newSessionFileEntries]);
        setTempFiles(prev => [...prev, ...newTempUiEntries]);
    }
    if (showFileSizeNotification) addNotification({ 
      type: "error", 
      title: "File size limit exceeded!", 
      message: largeFileNames.length > 1
        ? `${largeFileNames.length} files exceed the maximum file size of 20mb. Please remove them to proceed.`
        : `The file ${largeFileNames[0]} exceeds the maximum file size of 20mb. Please remove it to proceed.`
    });
    if (showFileLimitNotification) addNotification({ 
      type: "warn", 
      title: "Too many files!", 
      message: `You can only upload up to ${maxFiles} files for this section. Please remove ${excessFilesCount} files to proceed.`
    });
    if (fileInputRef.current) fileInputRef.current.value = "";

    // Update error state based on file issues
    const hasErrors = newSessionFileEntries.some(f => f.status === "too_large" || f.status === "too_many_files" || f.status === "failed");
    setIsFileUploadError(hasErrors);

    const filesToUpload = newSessionFileEntries.filter(f => f.status === "gettingurl");
    await Promise.all(filesToUpload.map(async (fileToUpload) => {
      try {
        const signedUrlArray = await getSignedUrl({ file_names: [fileToUpload.file.name], ...(isDemo && {max_age: 120}) });
        const signedUrl = signedUrlArray[0];
        setFiles(prev => prev.map(f => f.id === fileToUpload.id ? { ...f, status: "inprogress", signedUrl } : f));
        setUploadProgress(prev => ({ ...prev, [fileToUpload.file.name]: 0 }));
        await uploadFileReq(signedUrl, fileToUpload.file, (ev: AxiosProgressEvent) => {
          const total = ev.total ?? 0;
          if (total > 0) setUploadProgress(p => ({ ...p, [fileToUpload.file.name]: Math.round((ev.loaded * 100) / total) }));
        });
        setFiles(prev => prev.map(f => f.id === fileToUpload.id ? { ...f, status: "success" } : f));
        setUploadProgress(prev => ({ ...prev, [fileToUpload.file.name]: -1 }));
        // IMPORTANT: Update the URL in the corresponding tempFile entry after successful upload
        setTempFiles(prevTemps => prevTemps.map(tf => 
            tf.fileName === fileToUpload.file.name && tf.url === "" ? {...tf, url: signedUrl, status: "success"} : tf
        ));
      } catch (error) {
        setFiles(prev => prev.map(f => f.id === fileToUpload.id ? { ...f, status: "failed" } : f));
        setUploadProgress(prev => ({ ...prev, [fileToUpload.file.name]: -1 }));
        setIsFileUploadError(true);
      }
    }));
  };
  
  const onRemoveTempFile = (fileIdToRemove: string) => {
    const fileToRemove = tempFiles.find(f => f.id === fileIdToRemove)
    setTempFiles(prev => {
      const filteredFiles = prev.filter(({ id }) => id !== fileIdToRemove);
        const fileNameSet = new Set<string>();
        let newTempFiles = [] as AgentSourceFile[];
      // return prev.filter(f => f.id !== fileIdToRemove)
      prev.forEach(thisFile => {
        if(thisFile.id !== fileIdToRemove){
          let status = thisFile.status;
        if(thisFile.status === "duplicate_files" && thisFile.fileName === fileToRemove?.fileName){
          status = "success";
        }

         if (fileNameSet.has(thisFile.fileName)) {
            if (status !== "too_large" && status !== "too_many_files") {
              status = "duplicate_files";
            }
          } else {
            fileNameSet.add(thisFile.fileName);
          }

          newTempFiles.push({...thisFile, status})
        }
      })

      return newTempFiles
    });
    setFiles(prev => {
      // First remove the file being deleted
      const filteredFiles = prev.filter(f => f.id !== fileIdToRemove);
      
      // Calculate available slots after removal
      const currentValidCount = filteredFiles.filter(f => 
        f.status === "success" || 
        f.status === "inprogress" || 
        f.status === "gettingurl" || 
        f.status === "retrying"
      ).length + permanentFiles.length;
      
      const availableSlots = maxFiles - currentValidCount;

      const fileNameSet = new Set<string>();
      let newFilesList = [] as UploadedFile[];
      
      // Convert "too_many_files" to "success" if there's room
      let updatedAvailableSlots = availableSlots;

      filteredFiles.forEach(thisFile => {
        if(thisFile.id !== fileIdToRemove){
          let status = thisFile.status;
          if(thisFile.status === "duplicate_files" && thisFile.file.name === fileToRemove?.fileName){
          status = "success";
        }

         if (fileNameSet.has(thisFile.file.name)) {
            if (status !== "too_large" && status !== "too_many_files") {
              status = "duplicate_files";
            }
          } else {
            fileNameSet.add(thisFile.file.name);
          }

          newFilesList.push({...thisFile as any, status})
        }
      })
      
      // Check if any remaining files have errors
      const stillHasErrors = newFilesList.some(f => fileErrorTypes.has(f.status as string));
      setIsFileUploadError(stillHasErrors);
      
      return newFilesList;
    });
    setUploadProgress(prev => { const newProg = {...prev}; delete newProg[fileToRemove?.fileName as string]; return newProg; });
  };

  const onRemovePermanentFile = (fileName: string) => {
    setPermanentFiles(prev => prev.filter(f => f.fileName !== fileName));
  };

  const handleStartReassessment = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (isControlSpecificMode && control) {
        // Directly use the current tempFiles state, but filter out those with no URL (e.g. upload failed before URL obtained)
        const urlsToSave = tempFiles.filter(tf => tf.url && tf.url !== "").map(tf => tf.url);
        const namesToSave = tempFiles.filter(tf => tf.url && tf.url !== "").map(tf => tf.fileName || "Unknown");
        
        getCustomizeControlsActions().setDataState((prevControls) =>
          prevControls.map((c) => {
            if (c.uid === control.uid) {
              return {
                ...c,
                new_uploaded_files: urlsToSave,
                new_uploaded_file_names: namesToSave,
                // Save the modified permanent files state (important when files are deleted)
                source_urls: permanentFiles.map(pf => pf.url),
                file_names: permanentFiles.map(pf => pf.fileName || "Unknown")
              };
            }
            return c;
          })
        );
      }
      if(showSaveSuccess){
        setSaveStatus('success');
        setTimeout(() => {
          setSaveStatus('idle');
        }, 3000);
      }else{
        onClose();
      }
    } catch (error) {
      console.error("Error saving to control temp state:", error);
      addNotification({ type: "error", title: "Save Failed", message: "Could not save file changes." });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRetry = async (fileId: string) => {
    const fileToRetry = files.find(f => f.id === fileId);
    if (!fileToRetry) return;

    try {
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: "retrying" } : f));
      const signedUrlArray = await getSignedUrl({ file_names: [fileToRetry.file.name], ...(isDemo && {max_age: 120}) });
      const signedUrl = signedUrlArray[0];
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: "inprogress", signedUrl } : f));
      setUploadProgress(prev => ({ ...prev, [fileToRetry.file.name]: 0 }));
      await uploadFileReq(signedUrl, fileToRetry.file, (ev: AxiosProgressEvent) => {
        const total = ev.total ?? 0;
        if (total > 0) setUploadProgress(p => ({ ...p, [fileToRetry.file.name]: Math.round((ev.loaded * 100) / total) }));
      });
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: "success" } : f));
      setUploadProgress(prev => ({ ...prev, [fileToRetry.file.name]: -1 }));
      // IMPORTANT: Update the URL in the corresponding tempFile entry after successful upload
      setTempFiles(prevTemps => prevTemps.map(tf => 
          tf.fileName === fileToRetry.file.name && tf.url === "" ? {...tf, url: signedUrl} : tf
      ));
      
      // Check if any remaining files have errors
      setFiles(prev => {
        const stillHasErrors = prev.some(f => 
          f.id !== fileId && (f.status === "too_large" || f.status === "too_many_files" || f.status === "failed")
        );
        setIsFileUploadError(stillHasErrors);
        return prev;
      });
    } catch (error) {
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: "failed" } : f));
      setUploadProgress(prev => ({ ...prev, [fileToRetry.file.name]: -1 }));
      setIsFileUploadError(true);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
      <ModalHeader className="flex flex-col pb-2 px-6 border-b">
          <div className="text-center mb-3">
            <span className="text-lg font-semibold">Update Documents</span>
          </div>
          
          <div className="flex items-center w-full">
            <ZButton 
              isIconOnly 
              size="sm" 
              variant="light" 
              onClick={onPrevControl}
              isDisabled={!onPrevControl}
              className="p-0 min-w-0 w-6 h-6 flex-shrink-0"
            >
              <ChevronLeft size={16} />
            </ZButton>
            
            <div className="flex-grow px-2 w-full overflow-hidden">
              <ZTooltip content={controlName} placement="top" showArrow={true}>
                <div className="text-sm truncate text-center">
                  {controlName}
                </div>
              </ZTooltip>
            </div>
            
            <ZButton 
              isIconOnly 
              size="sm" 
              variant="light" 
              onClick={onNextControl}
              isDisabled={!onNextControl}
              className="p-0 min-w-0 w-6 h-6 flex-shrink-0"
            >
              <ChevronRight size={16} />
            </ZButton>
          </div>

          <ZTooltip content={controlDescription} placement="bottom" showArrow={true}>
            <div className="text-xs text-gray-500 mt-3 truncate text-center">
              {controlDescription}
            </div>
          </ZTooltip>         
        </ModalHeader>
        
        <ModalBody className="pt-4">
          {/* Custom file upload UI */}
          <div 
            className="bg-[#F7F7FA] p-2 flex items-center flex-col justify-center h-32 w-full border-dashed border-[1px] border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                fileInputRef.current?.click();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label="Upload files"
          >
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={handleFileChange}
              className="hidden"
              accept={allowedFileTypes.join(",")}
            />
            
            <Upload size={24} />
            
            <div className="cursor-pointer text-sm mt-4">
              Drag and drop files here or{" "}
              <span className="text-[#006FEE]">browse your computer</span>
            </div>
            <p className="text-xs text-center text-gray-500 mt-2 max-w-80">
              (Accepted files: Up to {Math.max(0, maxFiles - (permanentFiles.length + tempFiles.filter(f => {
                  // const fileObj = files.find(file => file.file.name === f.fileName);
                  return !f || !fileErrorTypes.has(f.status as string);
                }).length))} files of PDF, Excel, Word, JSON, CSV, or images up to 20mb)
            </p>
          </div>
          
         
          
          {/* Newly Added Files (Temporary) section */}
          {tempFiles.length > 0 && (
            <Accordion defaultExpandedKeys={["tempFiles"]} className="mt-4">
              <AccordionItem
                key="tempFiles"
                aria-label="temporary files"
                className="file-category-heading"
                title={
                  <span className="text-[14px] font-normal text-zinc-900">
                    Newly Added Files
                  </span>
                }
              >
                <div className="max-h-[150px] overflow-auto">
                  {/* Display files with errors first */}
                  {tempFiles.map((source) => {
                    const fileObjFromFilesState = source;
                    const status = fileObjFromFilesState?.status;
                    const isError = fileObjFromFilesState ? (doesFileHasError(fileObjFromFilesState as any) || status === 'failed') : false;
                    
                    // Only render error files in this first loop
                    if (!isError) return null;
                    
                    const currentProgress = uploadProgress[source.fileName || ""];
                    
                    const iconColor = "#e7000b"; // Error color
                    const textColor = "text-red-600";
                    const fileNameSuffix = getLocalFileNameSuffix(status);

                    return (
                      <div 
                        className="flex items-center justify-between gap-2 mt-0" 
                        key={`temp-error-${source.id}`}
                      >
                        <File
                          size={16}
                          className="ml-3 min-w-4"
                          color={iconColor}
                        />
                        <div className="flex-1 flex flex-col justify-center ">
                          <div className="flex items-center gap-1">
                            <p className={cn("text-sm cursor-pointer hover:underline max-w-full", textColor)}>
                              <ZFileName filename={source.fileName || "Unknown file"} />
                            </p>
                            <p className={cn("text-sm cursor-pointer hover:underline max-w-full whitespace-nowrap", textColor)}>
                              {fileNameSuffix}
                            </p>
                          </div>
                          <div>
                            {status !== "success" &&
                             currentProgress !== undefined &&
                             currentProgress !== -1 
                             ? (
                              <ZProgress
                                aria-label="file-upload-progress"
                                value={currentProgress || 0}
                                maxValue={100}
                                className={cn("h-[3px] mt-1 [&>*]:bg-blue-600")}
                              />
                            ) : null }
                          </div>
                        </div>
                        {(status === "failed" || status === "retrying") && fileObjFromFilesState ? (
                          <ZButton
                            isIconOnly
                            className="bg-transparent"
                            onClick={() => handleRetry(fileObjFromFilesState.id as string)}
                            isLoading={status === "retrying"}
                          >
                            <RotateCw size={16} color="gray" />
                          </ZButton>
                        ) : null}
                        <ZButton
                          isIconOnly
                          className="bg-transparent"
                          onClick={() => onRemoveTempFile(source.id || "")}
                        >
                          <Trash size={16} color="gray" />
                        </ZButton>
                      </div>
                    );
                  })}

                  {/* Then display valid files */}
                  {tempFiles.map((source) => {
                    const fileObjFromFilesState = source
                    const status = fileObjFromFilesState?.status;
                    const isError = fileObjFromFilesState ? (doesFileHasError(fileObjFromFilesState as any) || status === 'failed') : false;
                    
                    // Only render non-error files in this second loop
                    if (isError) return null;
                    
                    const currentProgress = uploadProgress[source.fileName || ""];
                    
                    let iconColor = "#22c55e"; // Default success color
                    if (status === "inprogress" || status === "gettingurl" || status === "retrying") iconColor = "#000";
                    
                    const textColor = (status === "success" || source.url) ? "text-green-600" : "text-black";
                    const fileNameSuffix = getLocalFileNameSuffix(status);

                    return (
                      <div 
                        className="flex items-center justify-between gap-2 mt-0"
                        key={`temp-valid-${source.id}`}
                      >
                        <File
                          size={16}
                          className="ml-3 min-w-4"
                          color={iconColor}
                        />
                        <div className="flex-1 flex flex-col justify-center ">
                          <div className="flex items-center gap-1">
                            <p className={cn("text-sm cursor-pointer hover:underline max-w-full", textColor)}>
                              <ZFileName filename={source.fileName || "Unknown file"} />
                            </p>
                            <p className={cn("text-sm cursor-pointer hover:underline max-w-full whitespace-nowrap", textColor)}>
                              {fileNameSuffix}
                            </p>
                          </div>
                          <div>
                            {status !== "success" &&
                             currentProgress !== undefined &&
                             currentProgress !== -1 
                             ? (
                              <ZProgress
                                aria-label="file-upload-progress"
                                value={currentProgress || 0}
                                maxValue={100}
                                className={cn("h-[3px] mt-1 [&>*]:bg-blue-600")}
                              />
                            ) : null }
                          </div>
                        </div>
                        {(status === "failed" || status === "retrying") && fileObjFromFilesState ? (
                          <ZButton
                            isIconOnly
                            className="bg-transparent"
                            onClick={() => handleRetry(fileObjFromFilesState.id as string)}
                            isLoading={status === "retrying"}
                          >
                            <RotateCw size={16} color="gray" />
                          </ZButton>
                        ) : null}
                        <ZButton
                          isIconOnly
                          className="bg-transparent"
                          onClick={() => onRemoveTempFile(source.id || "")}
                        >
                          <Trash size={16} color="gray" />
                        </ZButton>
                      </div>
                    );
                  })}
                </div>
              </AccordionItem>
            </Accordion>
          )}
          
          {/* Previously Added Files (Permanent) section */}
          {permanentFiles.length > 0 && (
            <Accordion defaultExpandedKeys={["permanentFiles"]} className="mt-4">
              <AccordionItem
                key="permanentFiles"
                aria-label="permanent files"
                className="file-category-heading"
                title={
                  <span className="text-[14px] font-normal text-zinc-900">
                    Previously Added Files
                  </span>
                }
              >
                <div className="max-h-[150px] overflow-auto">
                  {permanentFiles.map((source, index) => (
                    <div
                      className="flex justify-between items-center gap-2 mt-0 ml-3"
                      key={`perm-${index}-${source.fileName || source.url}`}
                    >
                      <div className="flex gap-2">
                        <File size={16} color="#22c55e" className="min-w-4" />
                        <p className="text-sm cursor-pointer hover:underline text-green-600">
                          <ZFileName filename={source.fileName || "Unknown file"} />
                        </p>
                      </div>
                      <div>
                        <ZButton
                          isIconOnly
                          className="bg-transparent"
                          onClick={() => onRemovePermanentFile(source.fileName || "")}
                        >
                          <Trash size={16} color="gray" />
                        </ZButton>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionItem>
            </Accordion>
          )}
        </ModalBody>
        
        <ModalFooter className="border-t pt-4">
          <ZButton color="default" variant="bordered" className="min-w-[80px]" onClick={onClose}>
            Cancel
          </ZButton>
          <ZButton
            color="primary"
            className="min-w-[80px] min-h-[40px]"
            onClick={handleStartReassessment}
            isLoading={isLoading}
            isDisabled={isLoading || isFileUploadError || files.some(f => fileErrorTypes.has(f.status as string))}
            startContent={(!isLoading && saveStatus === 'idle') &&  <Upload size={16} />}
          >
            {saveStatus === 'success' ? (
              <Check />
            ) : isControlSpecificMode ? "Save" : "Start Reassessment"}
          </ZButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReassessDocumentsModal;